import './MultiCodeInputComponentConfig';
import { useTheme } from '@mui/material';
import TextField from '@mui/material/TextField';
import { FC, useState, ClipboardEvent, useEffect, FocusEvent } from 'react';
import { useTranslation } from 'react-i18next';
import OTPInput from 'react-otp-input';
import * as yup from 'yup';

import AtomFlex from '../../../../../../../components/atoms/AtomFlex';
import AtomTypography from '../../../../../../../components/atoms/AtomTypography/AtomTypography';
import { styleConfigs } from '../../../../../../../components/resources/styleConfigs';
import BoxWrapper from '../../../../../../../components/wrappers/BoxWrapper';
import { MFAMessageSentEnum } from '../../../../../../../constants/enums/MFAMessageSentEnum';
import { IValidateMFACodeResponse } from '../../../../../../../operations/query/mfa/useValidateMFACode/useValidateMFACode';
import { COLORS } from '../../../../../../../theme/colors';

interface IMultiCodeInputComponentProps {
  setActiveSuperiorButton: (value: boolean) => void;
  setActiveCode: (value?: string) => void;
  validateMFACodeData?: IValidateMFACodeResponse | undefined;
}

const MultiCodeInputComponent: FC<IMultiCodeInputComponentProps> = (props) => {
  const { setActiveSuperiorButton, setActiveCode, validateMFACodeData } = props;
  const { t } = useTranslation('MultiCodeInputComponent');
  const theme = useTheme();
  const [otp, setOtp] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [MFAInvalidCode, setMFAInvalidCode] = useState<string>();

  const validationInput = (input: string): void => {
    const validationSchema = yup
      .string()
      .required(t('FORM_VALIDATION_FIELD_REQUIRED'))
      .length(6, t('FORM_VALIDATION_MAXIMUM_CHARACTERS'));

    validationSchema
      .validate(input)
      .then(() => setError(null))
      .catch((err) => setError(err.message));
  };

  const handleChangeValue = (val: string): void => {
    setOtp(val);
    setActiveCode(val);
    validationInput(val);
  };

  const handleOnBlur = (event: FocusEvent<HTMLInputElement>): void => {
    if (!event.relatedTarget && !document.hasFocus()) {
      return;
    }
    const input = event.target.value.trimStart();

    validationInput(input);
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>): void => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

    setOtp(pasted);
    setActiveCode(pasted);
    validationInput(pasted);
  };

  const handleNumericKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    const isCtrlCmd = e.ctrlKey || e.metaKey;
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];

    if (isCtrlCmd && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
      return;
    }

    if (!/\d/.test(e.key) && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleAttemptMessages = (attemptMessage?: string): string | undefined => {
    switch (attemptMessage) {
      case MFAMessageSentEnum.FIRST_ATTEMPT:
        return t('FORM_VALIDATION_FIRST_ATTEMPT');

      case MFAMessageSentEnum.SECOND_ATTEMPT:
        return t('FORM_VALIDATION_SECOND_ATTEMPT');

      case MFAMessageSentEnum.THIRD_ATTEMPT:
        return t('FORM_VALIDATION_THIRD_ATTEMPT');

      default:
        return undefined;
    }
  };

  useEffect(() => {
    if (otp.length > 0) {
      validationInput(otp);
    }
    setActiveSuperiorButton(!(otp.length === 6 && !error));
    setMFAInvalidCode(handleAttemptMessages());
  }, [otp, error]);

  useEffect(() => {
    if (otp.length === 6 && validateMFACodeData?.validateMFACode.success === false) {
      setActiveSuperiorButton(true);
      setMFAInvalidCode(handleAttemptMessages(validateMFACodeData?.validateMFACode.message));
    }
    if (otp.length < 1 && validateMFACodeData?.validateMFACode.success === false) {
      setActiveSuperiorButton(false);
    }
  }, [otp, validateMFACodeData]);

  return (
    <AtomFlex gap={styleConfigs.marginPadding.custom1}>
      <OTPInput
        value={otp}
        onChange={handleChangeValue}
        onPaste={handlePaste}
        numInputs={6}
        shouldAutoFocus
        containerStyle={{
          justifyContent: 'center',
          gap: styleConfigs.marginPadding.custom20,
        }}
        renderInput={(params, i) => (
          <TextField
            InputProps={{
              inputRef: params.ref,
              inputProps: {
                ...params,
                placeholder: t('FORM_VALIDATION_PLACEHOLDER_INPUT'),
                onBlur: i === 0 && !params.value ? handleOnBlur : undefined,
                onKeyDown: handleNumericKeyDown,
                inputMode: 'numeric',
                pattern: '[0-9]*',
                type: 'tel',
              },
            }}
            error={Boolean(MFAInvalidCode) || Boolean(error)}
            sx={{
              '& .MuiOutlinedInput-root': {
                width: `${styleConfigs.dimensions.width50}px`,
                height: `${styleConfigs.dimensions.height56}px`,
                borderRadius: styleConfigs.borders.radius0p5,
                textAlign: 'start',
                '& fieldset': {
                  border: `${styleConfigs.borders.borderLine1p5px} ${COLORS.jungleMist}`,
                },
                '&:hover fieldset': {
                  border: error
                    ? `${styleConfigs.borders.borderLine2px} ${COLORS.coralRed}`
                    : `${styleConfigs.borders.borderLine2px} ${theme.palette.background.redViolet}`,
                },
              },
              '& .MuiInputBase-input': {
                width: `${styleConfigs.dimensions.width26}px !important`,
                textAlign: params.value ? null : 'start !important',
                fontSize: styleConfigs.fonts.fontSize16,
                color: theme.palette.text.prussianBlue,
                py: styleConfigs.marginPadding.custom16px,
                px: styleConfigs.marginPadding.custom12px,
              },
            }}
          />
        )}
        renderSeparator={(i) =>
          i === 2 && (
            <BoxWrapper
              width={styleConfigs.dimensions.width12}
              height={styleConfigs.dimensions.height1p5}
              bgcolor={error ? COLORS.coralRed : COLORS.jungleMist}
            />
          )
        }
      />
      <AtomTypography
        variant="overline"
        fontWeight={500}
        textTransform="initial"
        color={COLORS.coralRed}
        paddingInlineStart={styleConfigs.marginPadding.custom1rem}
      >
        {MFAInvalidCode || error}
      </AtomTypography>
    </AtomFlex>
  );
};

export default MultiCodeInputComponent;
